<template>
  <div class="row">
    <div class="col-md-12 col-xxl-12 col-sm-12 mb-10">
      <div class="card mb-25 border-0 rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">

          <!-- En-tête -->
          <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 mb-4">
            <router-link to="/liste-devoirs" class="btn btn-primary transition border-0 lh-1 fw-medium">
              <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
              <span class="position-relative">Retour</span>
            </router-link>
            <h4 class="mb-0">Détails & Correction du devoir</h4>
          </div>

          <!-- Chargement -->
          <div v-if="!devoir" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <!-- Contenu principal -->
          <div v-else>
            <!-- Bloc Corriger le devoir -->
            <div v-if="devoir.statut !== 'corrige' && devoir.composition?.type !== 'QCM'" class="row mb-4">
              <div class="col-md-12">
                <div class="info-card p-3 border rounded bg-light-warning">
                  <h6 class="text-dark mb-3">
                    <i class="flaticon-edit me-2"></i>Corriger le devoir
                  </h6>
                  <form @submit.prevent="corrigerDevoir" class="row g-3 align-items-end">
                    <div class="col-md-2">
                      <label class="form-label fw-semibold">Note <span class="text-danger">*</span></label>
                      <input
                        v-model.number="form.note"
                        type="number"
                        min="0"
                        max="20"
                        step="0.5"
                        class="form-control uniform-field"
                        placeholder="0 à 20"
                        required
                      />
                    </div>
                    <div class="col-md-5">
                      <label class="form-label fw-semibold">Commentaire</label>
                      <textarea
                        v-model="form.commentaire"
                        class="form-control uniform-field"
                        placeholder="Ex: Bon travail..."
                      ></textarea>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">PDF corrigé</label>
                      <input
                        type="file"
                        accept=".pdf"
                        @change="onFileChange"
                        class="form-control uniform-field"
                      />
                    </div>
                    <div class="col-md-2">
                      <button
                        type="submit"
                        class="btn btn-success uniform-field w-100"
                        :disabled="isSubmitting"
                      >
                        <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                        Corriger
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>


            <!-- Informations générales -->
            <div class="row mb-4">
              <!-- Étudiant + Devoir -->
              <div class="col-md-6">
                <div class="info-card p-3 border rounded bg-light">
                  <h6 class="text-primary mb-3">
                    <i class="flaticon-user me-2"></i>Étudiant & Devoir
                  </h6>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Étudiant :</strong></div>
                    <div class="col-7"><strong>{{ devoir.etudiant?.prenom }} {{ devoir.etudiant?.nom }}</strong></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Filière :</strong></div>
                    <div class="col-7"><span class="badge bg-info">{{ devoir.composition?.filiereNiveauMatiere?.filiere?.libelle || 'N/A' }}</span></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Niveau :</strong></div>
                    <div class="col-7"><span class="badge bg-warning">{{ devoir.composition?.filiereNiveauMatiere?.niveau?.libelle || 'N/A' }}</span></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Fichier soumis :</strong></div>
                    <div class="col-7">
                      <div v-if="devoir.fichierNom" class="d-flex flex-column gap-2">
                        <div class="file-name-display mb-2">
                          <i class="flaticon-file me-2 text-danger"></i>
                          <small class="text-muted">{{ devoir.fichierNom }}</small>
                        </div>
                        <div class="d-flex gap-2">
                          <button @click="openPdfModal(devoir.fichierUrl, devoir.fichierNom)" class="btn btn-sm btn-outline-primary">
                            <i class="flaticon-eye me-1"></i>Voir
                          </button>
                          <button @click="downloadFile(devoir.id, 'original')" class="btn btn-sm btn-outline-success">
                            <i class="flaticon-download me-1"></i>Télécharger
                          </button>
                        </div>
                      </div>
                      <span v-else class="text-muted">Aucun fichier</span>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Soumis le :</strong></div>
                    <div class="col-7"><strong>{{ formatDate(devoir.dateSoumission) }}</strong></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Statut :</strong></div>
                    <div class="col-7">
                      <span :class="getStatutBadge(devoir.statut)" class="badge">{{ formatStatut(devoir.statut) }}</span>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Note :</strong></div>
                    <div class="col-7">
                      <strong v-if="devoir.note !== null && devoir.note !== undefined" class="text-success">{{ devoir.note }}/20</strong>
                      <span v-else class="text-muted">Non corrigé</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Composition -->
              <div class="col-md-6">
                <div class="info-card p-3 border rounded bg-light">
                  <h6 class="text-primary mb-3">
                    <i class="flaticon-exam me-2"></i>Composition
                  </h6>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Titre :</strong></div>
                    <div class="col-7"><strong>{{ devoir.composition?.titre || 'N/A' }}</strong></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Matière :</strong></div>
                    <div class="col-7"><span class="badge bg-primary">{{ devoir.composition?.filiereNiveauMatiere?.matiere?.libelle || 'N/A' }}</span></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Type :</strong></div>
                    <div class="col-7"><span class="badge bg-secondary">{{ devoir.composition?.type || 'N/A' }}</span></div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Date :</strong></div>
                    <div class="col-7">{{ formatDate(devoir.composition?.dateComposition) }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5"><strong>Professeur :</strong></div>
                    <div class="col-7">{{ devoir.composition?.professeur?.prenom }} {{ devoir.composition?.professeur?.nom }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fichier corrigé -->
            <div v-if="devoir.fichierCorrigeUrl" class="row mt-4">
              <div class="col-md-12">
                <div class="info-card p-3 border rounded bg-light">
                  <h6 class="text-success mb-3">
                    <i class="flaticon-check me-2"></i>Devoir corrigé
                  </h6>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="d-flex flex-column gap-2">
                        <div class="file-name-display mb-2">
                          <i class="flaticon-file me-2 text-success"></i>
                          <small class="text-muted">{{ devoir.fichierCorrigeNom }}</small>
                        </div>
                        <div class="d-flex gap-2">
                          <button @click="openPdfModal(devoir.fichierCorrigeUrl, 'Devoir corrigé')" class="btn btn-sm btn-outline-success">
                            <i class="flaticon-eye me-1"></i>Voir
                          </button>
                          <button @click="downloadFile(devoir.id, 'corrige')" class="btn btn-sm btn-outline-primary">
                            <i class="flaticon-download me-1"></i>Télécharger
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6" v-if="devoir.commentaire">
                      <strong>Commentaire :</strong>
                      <p class="mt-2 p-3 bg-white border rounded">{{ devoir.commentaire }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal PDF -->
    <div v-if="showPdfModal" class="modal fade show d-block pdf-modal-overlay" tabindex="-1" @click.self="closePdfModal">
      <div class="modal-dialog modal-xl modal-dialog-top">
        <div class="modal-content">
          <div class="modal-header bg-primary">
            <h5 class="modal-title text-white"><i class="flaticon-file me-2"></i>{{ currentPdfTitle }}</h5>
            <button type="button" class="btn-close btn-close-white" @click="closePdfModal"></button>
          </div>
          <div class="modal-body p-0 pdf-viewer-container">
            <iframe :src="currentPdfUrl" class="pdf-viewer-iframe" title="Visualiseur PDF"></iframe>
          </div>
          <div class="modal-footer">
            <!-- <a :href="currentPdfUrl" target="_blank" class="btn btn-outline-primary btn-sm">
              <i class="flaticon-external-link me-1"></i>Ouvrir dans un nouvel onglet
            </a> -->
            <button type="button" class="btn btn-secondary btn-sm" @click="closePdfModal">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { error, success } from "@/utils/utils";

export default defineComponent({
  name: "ViewDevoir",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const devoir = ref<any>(null);
    const showPdfModal = ref(false);
    const currentPdfUrl = ref('');
    const currentPdfTitle = ref('');

    // Formulaire de correction
    const form = ref({
      note: null as number | null,
      commentaire: '',
      fichierCorrige: null as File | null
    });
    const isSubmitting = ref(false);

    const getDevoir = async (id: string) => {
      try {
        const { data } = await ApiService.get(`/devoirs/${id}`);
        devoir.value = data.data;
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors du chargement du devoir");
      }
    };


    const openPdfModal = (filePath: string, title: string) => {
      currentPdfTitle.value = title;
      showPdfModal.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closePdfModal = () => {
      showPdfModal.value = false;
      currentPdfUrl.value = '';
      currentPdfTitle.value = '';
      document.body.style.overflow = '';
    };

    const downloadFile = async (devoirId: number, type: 'original' | 'corrige') => {
      try {
        const response = await ApiService.get(`/devoirs/${devoirId}/download?type=${type}`, {
          responseType: 'blob',
          timeout: 30000
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = type === 'corrige' ? devoir.value?.fichierCorrigeNom || 'corrige.pdf' : devoir.value?.fichierNom || 'devoir.pdf';
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        success("Téléchargement réussi");
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors du téléchargement");
      }
    };

    const onFileChange = (e: any) => {
      form.value.fichierCorrige = e.target.files[0] || null;
    };

    const corrigerDevoir = async () => {
        
      if (form.value.note === null || form.value.note < 0 || form.value.note > 20) {
        error("Note entre 0 et 20 requise");
        return;
      }

      isSubmitting.value = true;
      const formData = new FormData();
      formData.append('note', form.value.note.toString());
      if (form.value.commentaire) formData.append('commentaire', form.value.commentaire);
      if (form.value.fichierCorrige) formData.append('fichierCorrige', form.value.fichierCorrige);

      try {
        const { data } = await ApiService.put(`/devoirs/${devoir.value.id}/corriger`, formData);
        
        // Mise à jour locale
        devoir.value = { ...devoir.value, ...data.data };
        success("Devoir corrigé !");
        form.value = { note: null, commentaire: '', fichierCorrige: null };
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la correction");
      } finally {
        isSubmitting.value = false;
      }
    };

    const formatDate = (date: string | Date | null | undefined) => {
      if (!date) return 'N/A';
      try {
        return new Date(date).toLocaleString('fr-FR', {
          day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
      } catch (e) { return 'Date invalide'; }
    };

    const formatStatut = (statut: string) => {
      const map: Record<string, string> = { 'soumis': 'Soumis', 'en_cours': 'En cours', 'corrige': 'Corrigé' };
      return map[statut] || statut || 'N/A';
    };

    const getStatutBadge = (statut: string) => {
      const map: Record<string, string> = { 'soumis': 'bg-warning', 'en_cours': 'bg-info', 'corrige': 'bg-success' };
      return map[statut] || 'bg-secondary';
    };

    onMounted(() => {
      const id = route.params.id as string;
      if (id) getDevoir(id);
    });

    return {
      devoir,
      showPdfModal,
      currentPdfUrl,
      currentPdfTitle,
      openPdfModal,
      closePdfModal,
      downloadFile,
      form,
      isSubmitting,
      corrigerDevoir,
      onFileChange,
      formatDate,
      formatStatut,
      getStatutBadge,
    };
  },
});
</script>

<style scoped>
.pdf-modal-overlay {
  background: rgba(0, 0, 0, 0.5);
}
.pdf-viewer-iframe {
  width: 100%;
  height: 80vh;
  border: none;
}
.info-card {
  background-color: #f8f9fa;
}

.bg-light-warning {
  background-color: #fff8e1; 
}

.same-height {
  height: calc(2.4rem + 2px);
}

.form-control-sm,
.btn-sm {
  font-size: 0.875rem;
  padding: 0.4rem 0.6rem;
}

.uniform-field {
  height: 45px; 
  font-size: 0.95rem;
  border-radius: 6px;
  padding: 0 10px;
  box-sizing: border-box;
}

/* textarea.uniform-field {
  resize: none;
  height: 45px;
} */

.uniform-field.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.btn-success {
  background-color: #28a745;
  border: none;
}

.btn-success:hover {
  background-color: #23913d;
}
</style>